<template>
    <div class="col-12">
       <div>
          <div
            class="catalog-side-card"
            :class="{'full-text': fullText}"
            :style="{transform: 'none', opacity: (disabled ? 0.4 : 1)}"
            >
             <div class="catalog-side-card__wrapper">
                <div class="side-card-preview" v-if="!this.hideThumbnail">
                    <img :src="thumbSrc"
                        :alt="thumbAlt"
                        class="preview">
                </div>
                <div class="catalog-side-card__wrapper__container">
                   <div class="side-card-content">
                      <div class="side-card-content__left-container">
                         <div class="side-card-info">
                            <div class="side-card-info__title">
                                <span>{{ title }}</span>
                            </div>
                            <div class="side-card-info__desc" ref="sideCardDesc">
                               <span>
                                  <div>{{ description }}</div>
                               </span>
                            </div>
                            <div class="side-card-info__caption">
                               <span>
                                  <div>
                                     <div class="identifier" v-if="!this.hideIdentifier">
                                        {{ identifier }}
                                    </div>
                                     <div>
                                        <small v-if="missingReference">
                                            <div class="alert alert-danger small p-0" role="alert">
                                                Missing OGC reference metadata
                                            </div>
                                        </small>
                                     </div>
                                     <div
                                        v-if="!this.hideExpand"
                                        ref="descriptionRuler"
                                        class="ms-ruler"
                                        style="visibility: hidden; height: 0px; white-space: nowrap; position: absolute;">
                                        {{ description }}
                                    </div>
                                  </div>
                               </span>
                            </div>
                         </div>
                      </div>
                      <div class="side-card-content__right-container">
                         <div class="side-card-tool text-center">
                            <div class="btn-group">
                                <button
                                    type="button"
                                    class="btn btn-sm btn-main min-w-auto btn-add"
                                    data-toggle="tooltip-city"
                                    title="Adicionar ao mapa"
                                    @click="onLayerAdd">
                                    <span class="fas fa-plus"></span>
                                </button>
                                <button
                                    type="button"
                                    v-if="visibleExpand"
                                    data-toggle="tooltip-city"
                                    :title="fullText ? 'Collapse' : 'Expand'"
                                    class="btn btn-sm btn-main min-w-auto"
                                    @click="onFullText"
                                >
                                    <span class="fas" :class="fullText ? 'fa-chevron-down' : 'fa-chevron-left'"></span>
                                </button>
                            </div>
                         </div>
                         <div class="side-card-loading">
                            <div class="side-card-loader"></div>
                         </div>
                      </div>
                   </div>
                </div>
             </div>
          </div>
       </div>
    </div>
</template>

<script>
    import GlobalMixin from '@/app/shared/mixins/global';
    import { isArray, isObject, head } from '@config/utils';
    import { isAllowedSRS } from '../utils/CoordinatesUtils';
    import SERVICES from '../services';
    import NoThumbnailIcon from '@images/no-thumbnail.png';

    import {
        buildSRSMap,
        extractEsriReferences,
        extractOGCServicesReferences,
    } from '../utils/CatalogUtils';

    let defaultBackgroundThumbs=null;

    export default {
        name: 'CatalogRecordItem',
        mixins: [
            GlobalMixin,
        ],
        props: {
            addAuthentication: {
                type: Boolean,
            },
            authkeyParamNames: {
                type: Array,
            },
            catalogURL: {
                type: String,
            },
            catalogType: {
                type: String,
            },
            map: {
                type: Object,
                default: null,
            },
            crs: {
                type: String,
                default: 'EPSG:3857',
            },
            currentLocale: {
                type: String,
                default: 'en-US',
            },
            hideThumbnail: {
                type: Boolean,
                default: false,
            },
            hideExpand: {
                type: Boolean,
                default: false,
            },
            hideIdentifier: {
                type: Boolean,
                default: false,
            },
            layerBaseConfig: {
                type: Object,
                default: () => {},
            },
            record: {
                type: Object,
                required: true,
                default: () => {},
            },
            showGetCapLinks: {
                type: Boolean,
                default: false,
            },
            zoomToLayer: {
                type: Boolean,
                default: true,
            },
            layers: {
                type: Array,
                default: () => [],
            },
            source: {
                type: String,
            },
            deletedId: {
                type: String,
            },
            service: {
                type: Object,
                default: () => {},
            },
            showTemplate: {
                type: Boolean,
                default: false,
            },
            defaultFormat: {
                type: String,
                default: 'image/png',
            },
            formatOptions: {
                type: Array,
                default: () => [],
            },
            infoFormatOptions: {
                type: Array,
                default: () => [],
            },
        },
        data() {
            return {
                idPanel: 'catalog',
                fullText: false,
                displayExpand: false,
                notAvailable: 'Not Available',
            }
        },
        mounted() {
            this.$nextTick(() => {
                this.applyDisplayExpand();
            });
        },
        computed: {
            noThumbnailIcon() {
                return NoThumbnailIcon;
            },
            defaultThumb() {
                return this.noThumbnailIcon;
            },
            title() {
                let title = this.record.title;
                return isObject(title) ? title[this.currentLocale] || title.default : title || '';
            },
            description() {
                return this.fullText && this.record.metadataTemplate ? 'xpto' : this.record.metadataTemplate ? '' : (isArray(this.record.description) ? this.record.description.join(", ") : this.record.description)
            },
            background() {
                return this.record && this.record.background;
            },
            disabled() {
                return this.background && head((this.layers || []).filter(layer => layer.id === this.background.name ||
                layer.type === this.background.type && layer.source === this.background.source && layer.name === this.background.name));
            },
            thumbAlt() {
                return this.title;
            },
            thumbSrc() {
                return this.record && this.record.thumbnail ||
                        this.background && defaultBackgroundThumbs[this.background.source][this.background.name] ||
                        this.defaultThumb;
            },
            identifier() {
                return this.record.identifier;
            },
            ogcReferences() {
                const { wms, wmts, tms, wfs } = extractOGCServicesReferences(this.record);
                return { wms, wmts, tms, wfs };//[wms, wmts, tms, wfs];
            },
            esriReferences() {
                const { esri } = extractEsriReferences(this.record);
                return { esri };
            },
            tileProvider() {
                return this.record.type === "tileprovider" && this.record.provider;
            },
            missingReference() {
                return !!![...Object.values(this.ogcReferences), ...Object.values(this.esriReferences)].find(o=> !!o);
            },
            visibleExpand() {
                return !this.hideExpand && this.displayExpand;
            },
        },
        methods: {
            onLayerAdd() {
                this.hideTooltip();
                let record = this. record;

                const links = this.showGetCapLinks ? getRecordLinks(record) : [];
                const formats = this.getFormats(record.layerType || record.serviceType, record) || [];
                const localizedLayerStyles = this.service?.localizedLayerStyles;
                const autoSetVisibilityLimits = this.service?.autoSetVisibilityLimits;
                const allowUnsecureLayers = this.service?.allowUnsecureLayers;

                const ogcReferences = this.ogcReferences || { SRS: [] };
                const allowedSRS = ogcReferences?.SRS?.length > 0 && buildSRSMap(ogcReferences.SRS);
                if (allowedSRS && !isAllowedSRS(this.crs, allowedSRS)) {
                    this.$emit('onError', `${this.idPanel}.srs_not_allowed`);
                    return null;
                }

                // this.setState({ loading: true });
                return SERVICES[record.serviceType].getLayerFromRecord(record, {
                    service: this.service,
                    layerBaseConfig: this.layerBaseConfig,
                    removeParams: this.authkeyParamNames,
                    catalogURL: this.catalogType === "csw" && this.catalogURL
                        ? this.catalogURL +
                        "?request=GetRecordById&service=CSW&version=2.0.2&elementSetName=full&id=" +
                        record.identifier
                        : null,
                    format: this.getLayerFormat(
                        formats.filter(f => f.indexOf("image/") === 0)
                    ),
                    formats: {
                        imageFormats: record.formats || this.formatOptions,
                        infoFormats: record.infoFormats || this.infoFormatOptions
                    },
                    ...(autoSetVisibilityLimits && {
                        map: {
                            projection: this.crs,
                            resolutions: getResolutions()
                        }
                    }),
                    localizedLayerStyles,
                    allowUnsecureLayers
                }, true)
                .then((layer) => {
                    if (layer) {
                        this.addLayer(layer, record);
                    }
                    // this.setState({ loading: false });
                });


                // const { wms, wmts, tms, wfs } = this.ogcReferences;
                // const {esri} = this.esriReferences;
                // if(wms || wmts){
                //     const type = wms ? 'wms' : 'wmts';
                //     const layer = this.makeLayer(type, wms || wmts, {imageFormats: this.getFormats(type), infoFormats: this.getInfoFormats(type)});
                //     if (layer) {
                //         this.addLayer(layer, {rcord: this.record});
                //     }
                // }else if (esri) {
                //     this.addLayer(esriToLayer(this.record, this.layerBaseConfig));
                // }else if (tms) {

                // }else if (wfs) {

                // }
            },
            onFullText() {
                this.hideTooltip();
                this.fullText = !this.fullText;
            },
            getFormats(type, record) {
                let formats;
                if (type === 'wms') {
                    formats = this.service?.format && [this.service.format];
                }
                return formats ? formats : record.format && [record.format] || record.formats.map(({value}) => value) || [];
            },
            // getInfoFormats(type) {
            //     return (this.record.infoFormats || []).map(({value}) => value);
            // },
            getLayerFormat(formats) {
                if (formats.length === 0 || formats.filter(f => f === this.defaultFormat).length > 0) {
                    return this.defaultFormat;
                }
                return formats[0];
            },
            // makeLayer(type, ogcReferences, formats = {imageFormats: this.formatOptions, infoFormats: this.infoFormatOptions}) {
            //     const allowedSRS = buildSRSMap(ogcReferences.SRS);
            //     if (ogcReferences.SRS.length > 0 && !isAllowedSRS(this.crs, allowedSRS)) {
            //         this.$emit('onError', 'The map coordinate system is not supported by this service');
            //         return null;
            //     }

            //     const imageFormats = formats.imageFormats;
            //     const localizedLayerStyles = this.service && this.service.localizedLayerStyles;
            //     console.log(type)
            //     return SERVICES[type].recordToLayer(
            //         this.record,
            //         {
            //             map: this.map,
            //             removeParams: this.authkeyParamNames,
            //             ...(type === "wms"
            //                 ? {
            //                     catalogURL:
            //                         this.catalogType === "csw" &&
            //                         this.catalogURL
            //                             ? this.catalogURL +
            //                             "?request=GetRecordById&service=CSW&version=2.0.2&elementSetName=full&id=" +
            //                             this.record.identifier
            //                             : null,
            //                     format: this.getLayerFormat(
            //                         imageFormats.filter(f => f.indexOf("image/") === 0)
            //                     ),
            //                     formats
            //                 }
            //                 : {
            //                     format: this.getLayerFormat(
            //                         imageFormats.filter(f => f.indexOf("image/") === 0)
            //                     )
            //                 }),
            //             layerBaseConfig: this.layerBaseConfig,
            //             localizedLayerStyles
            //         },
            //     );
            // },
            addLayer(layer) {
                const zoomToLayer = this.zoomToLayer;
                this.$emit('onLayerAdd', layer, { zoomToLayer })
            },
            applyDisplayExpand() {
                const descriptionRulerWidth = this.$refs['descriptionRuler'] ? this.$refs['descriptionRuler'].clientWidth : 0;
                const descriptionWidth = this.$refs['sideCardDesc'] ? this.$refs['sideCardDesc'].clientWidth : 0;
                this.displayExpand = descriptionRulerWidth > descriptionWidth;
            },


        }
    }
</script>

<style scoped lang="scss">

</style>